import { useCallback, useEffect, useMemo, useState } from "react";
import type { MaintenanceProfile } from "@/lib/maintenance";
import {
  loadLog,
  saveLog,
  newEntryId,
  todayIso,
  completedAtMilestone,
  lastCompletionByService,
  type LogEntry,
} from "@/lib/maintenance-log";

const miles = (n: number) => `${n.toLocaleString()} mi`;

const formatDate = (iso: string) => {
  const [y, m, d] = iso.split("-").map(Number);
  if (!y || !m || !d) return iso;
  return new Date(y, m - 1, d).toLocaleDateString(undefined, {
    year: "numeric",
    month: "short",
    day: "numeric",
  });
};

/** Log state persisted to this device, shared by the timeline and the log panel. */
export function useMaintenanceLog(vehicleKey: string) {
  const [entries, setEntries] = useState<LogEntry[]>([]);
  const [hydrated, setHydrated] = useState(false);

  useEffect(() => {
    setEntries(loadLog(vehicleKey));
    setHydrated(true);
  }, [vehicleKey]);

  const persist = useCallback(
    (next: LogEntry[]) => {
      const sorted = [...next].sort((a, b) => b.date.localeCompare(a.date));
      setEntries(sorted);
      saveLog(vehicleKey, sorted);
    },
    [vehicleKey],
  );

  const addEntry = useCallback(
    (entry: Omit<LogEntry, "id">) => persist([{ ...entry, id: newEntryId() }, ...entries]),
    [entries, persist],
  );

  const removeEntry = useCallback(
    (id: string) => persist(entries.filter((e) => e.id !== id)),
    [entries, persist],
  );

  const toggleMilestoneService = useCallback(
    (service: string, milestone: number, done: boolean) => {
      if (done) {
        persist([
          {
            id: newEntryId(),
            service,
            date: todayIso(),
            odometer: milestone,
            milestone,
            notes: "",
          },
          ...entries,
        ]);
      } else {
        persist(entries.filter((e) => !(e.service === service && e.milestone === milestone)));
      }
    },
    [entries, persist],
  );

  const completedByMilestone = useMemo(() => {
    const map = new Map<number, Set<string>>();
    for (const e of entries) {
      if (e.milestone == null) continue;
      const set = map.get(e.milestone) ?? new Set<string>();
      set.add(e.service);
      map.set(e.milestone, set);
    }
    return map;
  }, [entries]);

  return {
    entries,
    hydrated,
    addEntry,
    removeEntry,
    toggleMilestoneService,
    completedByMilestone,
    completedAtMilestone: (m: number) => completedAtMilestone(entries, m),
    lastByService: lastCompletionByService(entries),
  };
}

export function MaintenanceLogPanel({
  profile,
  fullName,
  log,
}: {
  profile: MaintenanceProfile;
  fullName: string;
  log: ReturnType<typeof useMaintenanceLog>;
}) {
  const services = useMemo(() => profile.items.map((i) => i.service), [profile]);
  const [service, setService] = useState(services[0] ?? "");
  const [customService, setCustomService] = useState("");
  const [date, setDate] = useState(todayIso());
  const [odometer, setOdometer] = useState("");
  const [notes, setNotes] = useState("");

  const submit = (e: React.FormEvent) => {
    e.preventDefault();
    const name = service === "__other" ? customService.trim() : service;
    if (!name || !date) return;
    const odo = Number(odometer.replace(/[^0-9]/g, ""));
    log.addEntry({
      service: name,
      date,
      odometer: odo > 0 ? odo : null,
      milestone: null,
      notes: notes.trim(),
    });
    setNotes("");
    setCustomService("");
  };

  const inputClass =
    "w-full rounded-md border border-border bg-background px-3 py-2 text-sm text-foreground outline-none focus:border-primary";

  return (
    <div className="border-t border-border p-4 sm:p-5">
      <h3 className="font-semibold text-foreground">
        {fullName} maintenance log
      </h3>
      <p className="mt-1 text-sm text-muted-foreground">
        Record what you've had done, with the date, mileage and any notes. Logged
        services are ticked off on the timeline and push the next due mileage forward.
        Everything stays on this device — no account needed.
      </p>

      <form onSubmit={submit} className="mt-4 grid gap-3 sm:grid-cols-2">
        <label className="text-sm">
          <span className="mb-1 block text-muted-foreground">Service</span>
          <select
            value={service}
            onChange={(e) => setService(e.target.value)}
            className={inputClass}
          >
            {services.map((s) => (
              <option key={s} value={s}>
                {s}
              </option>
            ))}
            <option value="__other">Other (type your own)</option>
          </select>
        </label>

        <label className="text-sm">
          <span className="mb-1 block text-muted-foreground">Date completed</span>
          <input
            type="date"
            value={date}
            onChange={(e) => setDate(e.target.value)}
            className={inputClass}
            required
          />
        </label>

        {service === "__other" && (
          <label className="text-sm sm:col-span-2">
            <span className="mb-1 block text-muted-foreground">Service name</span>
            <input
              value={customService}
              onChange={(e) => setCustomService(e.target.value)}
              placeholder="e.g. Serpentine belt replacement"
              className={inputClass}
              required
            />
          </label>
        )}

        <label className="text-sm">
          <span className="mb-1 block text-muted-foreground">Odometer (optional)</span>
          <input
            inputMode="numeric"
            value={odometer}
            onChange={(e) => setOdometer(e.target.value)}
            placeholder="e.g. 62,500"
            className={inputClass}
          />
        </label>

        <label className="text-sm">
          <span className="mb-1 block text-muted-foreground">Notes (optional)</span>
          <input
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            placeholder="Shop, parts used, cost…"
            className={inputClass}
          />
        </label>

        <div className="sm:col-span-2">
          <button
            type="submit"
            className="rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground hover:opacity-90"
          >
            Add to log
          </button>
        </div>
      </form>

      <h4 className="mt-6 font-medium text-foreground">
        Service history{log.entries.length ? ` (${log.entries.length})` : ""}
      </h4>
      {!log.hydrated ? null : log.entries.length === 0 ? (
        <p className="mt-2 text-sm text-muted-foreground">
          Nothing logged yet. Add a service above, or tick items off directly on the
          timeline.
        </p>
      ) : (
        <ul className="mt-3 space-y-2">
          {log.entries.map((e) => (
            <li
              key={e.id}
              className="flex flex-wrap items-start justify-between gap-3 rounded-md border border-border p-3"
            >
              <div>
                <p className="text-sm font-medium text-foreground">
                  {e.service}
                  {e.milestone != null && (
                    <span className="ml-2 rounded bg-secondary px-1.5 py-0.5 text-xs font-normal text-muted-foreground">
                      {miles(e.milestone)} milestone
                    </span>
                  )}
                </p>
                <p className="text-sm text-muted-foreground">
                  {formatDate(e.date)}
                  {e.odometer != null ? ` · ${miles(e.odometer)}` : ""}
                </p>
                {e.notes && (
                  <p className="mt-1 text-sm text-foreground/80">{e.notes}</p>
                )}
              </div>
              <button
                type="button"
                onClick={() => log.removeEntry(e.id)}
                className="text-sm text-muted-foreground underline hover:text-destructive"
                aria-label={`Remove ${e.service} logged on ${formatDate(e.date)}`}
              >
                Remove
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
